import { Fragment, useEffect, useMemo, useState } from "react";
import locale from "@bonliva-crm/locale";
import clsx from "clsx";
import { CloseIcon, DividerWithText, useSnackbarsPipeline } from "@bonliva-crm/ui";
import {
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  makeStyles,
  Typography
} from "@material-ui/core";
import {
  SearchProfileType,
  SearchSearchProfiles,
  useUpsertSearchProfileMutation
} from "@graphql";
import { UpsertSearchProfileModal } from "./search-profile/upsert-search-profile-modal";
import {
  filterPropsFactory,
  getIsSelected,
  getSearchComponentMap,
  SearchComponentData
} from "./utils/filters-record";
import { UseSearchFilters } from "./use-search-filters";
import { SearchFilterNames } from "./utils/filters-record";

type Props<T extends object> = {
  filterManager: UseSearchFilters<T>;
  searchProfileType: SearchProfileType;
  sortByLabel?: boolean;
  sortByFn?: (a: FilterData, b: FilterData) => number;
};

type FilterData = {
  key: SearchFilterNames;
} & SearchComponentData;

export function FilterSlider<T extends object>({
  filterManager,
  searchProfileType,
  sortByLabel = false,
  sortByFn
}: Props<T>) {
  const classes = useStyles();
  const snack = useSnackbarsPipeline();
  const SearchComponentMap = useMemo(() => getSearchComponentMap(), []);
  const [selectedFilters, setSelectedFilters] = useState<FilterData[]>([]);
  const [restFilters, setRestFilters] = useState<FilterData[]>([]);

  const [upsertSearchProfileMutation] = useUpsertSearchProfileMutation({
    refetchQueries: [SearchSearchProfiles.OperationName]
  });

  const filtersProps = filterPropsFactory({
    filters: filterManager.activeFilters,
    favoriteFilters: filterManager.favoriteFilters,
    setFavoriteFilters: filterManager.setFavoriteFilters,
    setSearchProfileModalProps: filterManager.setSearchProfileModalProps,
    overrides: filterManager.overrides,
    searchProfiles: filterManager.searchProfiles,
    setSearchProfiles: filterManager.setSearchProfiles,
    setSearchProfileFull: filterManager.setSearchProfileFull,
    withFavourite: true,
    searchProfileType
  });

  const upsertSearchProfile = () => {
    if (!filterManager.searchProfileFull) return;
    upsertSearchProfileMutation({
      variables: {
        id: filterManager.searchProfileFull._id,
        data: {
          fields: filterManager.searchProfileFields
        }
      }
    }).then(() => {
      snack.showSuccess(locale.SearchProfileSaved);
    });
  };

  const values = filterManager.formMethods.watch();

  useEffect(() => {
    const selectedFilters = getIsSelected(values, filterManager.searchProfiles);

    const sortByFunc = sortByFn ?? (sortByLabel ? sortByLabelFn : void 0);

    // an array of selected filter keys
    const selected: FilterData[] = filterManager.activeFilters
      .map((key) =>
        selectedFilters[key] && SearchComponentMap[key]
          ? {
              key,
              params: SearchComponentMap[key].params,
              component: SearchComponentMap[key].component
            }
          : null
      )
      .filter((x) => !!x);

    if (sortByFunc) {
      selected.sort(sortByFunc);
    }

    // an array of unselected filter keys
    const rest: FilterData[] = filterManager.activeFilters
      .map((key) =>
        !selectedFilters[key] && SearchComponentMap[key]
          ? {
              key,
              params: SearchComponentMap[key].params,
              component: SearchComponentMap[key].component
            }
          : null
      )
      .filter((x) => !!x);

    if (sortByFunc) {
      rest.sort(sortByFunc);
    }

    setSelectedFilters(selected);
    setRestFilters(rest);
  }, [filterManager.filterSliderOpen]);

  return (
    <>
      <Drawer
        anchor="right"
        open={filterManager.filterSliderOpen}
        onClose={() => filterManager.setFilterSliderOpen(false)}
      >
        <Box className={classes.root}>
          <Box className={classes.topBar}>
            <Typography variant="h3">{locale.Filters}</Typography>
            <IconButton
              className={classes.closeButton}
              onClick={() => filterManager.setFilterSliderOpen(false)}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider style={{ marginTop: "1rem" }} />
          <Box className={classes.scrollContainer}>
            <Box className={clsx(classes.container, classes.hideBadges)}>
              {selectedFilters.map((data, i) => {
                const { component: Component, params } = data;

                return (
                  <Fragment key={data.key}>
                    <Component {...filtersProps[data.key]} {...params} />
                    {i < selectedFilters.length - 1 && <Divider />}
                  </Fragment>
                );
              })}

              {selectedFilters.length > 0 && restFilters.length > 0 && (
                <DividerWithText>
                  <Typography variant={"body1"} noWrap={true}>
                    {locale.notSelected}
                  </Typography>
                </DividerWithText>
              )}

              {restFilters.map((data) => {
                const { component: Component, params } = data;

                return (
                  <Fragment key={data.key}>
                    <Component {...filtersProps[data.key]} {...params} />
                    <Divider />
                  </Fragment>
                );
              })}
            </Box>

            <Box className={classes.bottomContainer}>
              <Divider />

              <Box className={classes.buttonContainer}>
                <Button
                  onClick={() => filterManager.resetFilters()}
                  variant={filtersProps.searchProfile?.value ? "text" : "outlined"}
                  color="primary"
                  style={
                    filtersProps.searchProfile?.value
                      ? { backgroundColor: "transparent", boxShadow: "unset" }
                      : undefined
                  }
                  fullWidth={true}
                >
                  {locale.resetFilters}
                </Button>

                {filtersProps.searchProfile?.value && (
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => upsertSearchProfile()}
                    fullWidth={true}
                  >
                    {locale.UpdateSearchProfile}
                  </Button>
                )}

                <Button
                  variant="contained"
                  color="primary"
                  onClick={() =>
                    filterManager.setSearchProfileModalProps({
                      open: true,
                      mode: "new",
                      currentSearchProfile: null
                    })
                  }
                  fullWidth={true}
                >
                  {locale.SaveSearchProfile}
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Drawer>
      <UpsertSearchProfileModal
        {...filterManager.searchProfileModalProps}
        setOpen={(open) =>
          filterManager.setSearchProfileModalProps({
            ...filterManager.searchProfileModalProps,
            open
          })
        }
        fields={filterManager.searchProfileFields}
        type={searchProfileType}
        onlyName={searchProfileType === SearchProfileType.Kits}
      />
    </>
  );
}

function sortByLabelFn(a: FilterData, b: FilterData) {
  const aLabel = a.params.label ?? "";
  const bLabel = b.params.label ?? "";

  return aLabel.localeCompare(bLabel, "sv-SE");
}

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: "white",
    height: "100%",
    minWidth: "500px",
    maxWidth: "25vw",
    position: "relative",
    padding: theme.spacing(4),
    display: "flex",
    flexDirection: "column"
  },
  topBar: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center"
  },
  closeButton: {
    padding: 0
  },
  container: {
    display: "flex",
    flexDirection: "column",
    // gap: "0.5rem",
    height: "100%",
    paddingRight: theme.spacing(2),
    paddingBottom: theme.spacing(2)
  },
  scrollContainer: {
    display: "block",
    minHeight: "88%",
    maxHeight: "88%",
    overflowY: "auto",
    marginRight: -theme.spacing(3)
  },
  bottomContainer: {
    marginTop: "auto",
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
    zIndex: 10,
    position: "absolute",
    bottom: "1rem",
    width: "calc(100% - 4rem)"
  },
  buttonContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "0.5rem"
  },
  hideBadges: {
    "& .fav-filter-badge": {
      display: "none"
    }
  }
}));
